import { useCallbackRef } from "@hooks/use-callback-ref";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { CarType, SelectedOption } from "@types_def/models/delivery.types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useCalculateDeliveryPrice } from "../hooks/useCalculateDeliveryPrice";

type TDeliveryPrice = {
  title: string;
  fromPlaceId: string;
  toPlaceId: string;
  carType: CarType;
  category: string;
  numberOfVehicles: number;
  options?: { selected: SelectedOption[] };
  fromCity: string;
  toCity: string;
  onPriceCalculated: (price: number) => void;
};

function DeliveryPrice({
  title,
  fromPlaceId,
  toPlaceId,
  fromCity,
  toCity,
  category,
  numberOfVehicles,
  options,
  carType,
  onPriceCalculated,
}: TDeliveryPrice) {
  const { t } = useTranslation();

  const { data: priceData } = useCalculateDeliveryPrice({
    origin: fromPlaceId,
    destination: toPlaceId,
    category,
    numberOfCars: numberOfVehicles,
    options: options?.selected ?? [],
    carType,
  });

  const priceEffect = useCallbackRef(() => {
    if (priceData) {
      onPriceCalculated(Number(priceData.price));
    }
  });
  useEffect(() => {
    priceEffect();
  }, [priceData, priceEffect]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography variant="h6" textTransform="capitalize">
                {title}
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell component="th" scope="row" sx={{ textTransform: "capitalize" }}>
              {t("delivery.form.departure-city")}
            </TableCell>
            <TableCell align="right">{fromCity || "--------"}</TableCell>
          </TableRow>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell component="th" scope="row" sx={{ textTransform: "capitalize" }}>
              {t("delivery.form.arrival-city")}
            </TableCell>
            <TableCell align="right">{toCity || "--------"}</TableCell>
          </TableRow>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell component="th" scope="row" sx={{ textTransform: "capitalize" }}>
              {t("delivery.form.distance")}
            </TableCell>
            <TableCell align="right">{priceData ? priceData.distance?.text : "--------"}</TableCell>
          </TableRow>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell component="th" scope="row" sx={{ textTransform: "capitalize" }}>
              {t("delivery.form.duration")}
            </TableCell>
            <TableCell align="right">{priceData ? priceData.duration?.text : "--------"}</TableCell>
          </TableRow>
          <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell component="th" scope="row" sx={{ textTransform: "capitalize" }}>
              <strong>{t("delivery.form.delivery-price")}</strong>
            </TableCell>
            <TableCell align="right">
              <strong>{priceData ? `${priceData.price} €` : "--------"}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DeliveryPrice;
